import React, { useEffect, useState } from 'react';
import { useNavigate, useParams }     from 'react-router-dom';
import { useAuth }                    from '../contexts/AuthContext';
import { getVehicle, updateVehicle }  from '../api/vehicles';
import './EditProjectPage.css';

export default function EditProjectPage() {
  const { vehicleId }   = useParams();
  const { user, signOut } = useAuth();
  const navigate          = useNavigate();

  const [form,    setForm]    = useState({
    nickname: '', vin: '', engine_desc: '', transmission_desc: '',
  });
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    getVehicle(vehicleId)
      .then(r => {
        if (r.success) {
          setVehicle(r.data);
          setForm({
            nickname:          r.data.nickname          || '',
            vin:               r.data.vin               || '',
            engine_desc:       r.data.engine_desc       || '',
            transmission_desc: r.data.transmission_desc || '',
          });
        }
      })
      .catch(() => setError('Could not load vehicle.'))
      .finally(() => setLoading(false));
  }, [vehicleId]);

  async function handleSave() {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const res = await updateVehicle(vehicleId, {
        nickname:          form.nickname          || undefined,
        vin:               form.vin               || undefined,
        engine_desc:       form.engine_desc       || undefined,
        transmission_desc: form.transmission_desc || undefined,
      });
      if (!res.success) throw new Error(res.message || 'Save failed');

      // Update cached active vehicle if it matches
      const cached = localStorage.getItem('ar_active_vehicle');
      if (cached) {
        const v = JSON.parse(cached);
        if (String(v.vehicleId) === String(vehicleId)) {
          localStorage.setItem('ar_active_vehicle', JSON.stringify({
            ...v, ...form,
          }));
        }
      }

      setSuccess('Project updated successfully!');
      setTimeout(() => navigate('/projects'), 1200);
    } catch (err) {
      setError(err.message || 'Could not save changes.');
    } finally {
      setSaving(false);
    }
  }

  function handleSignOut() { signOut(); navigate('/'); }

  const vehicleLabel = vehicle
    ? `${vehicle.year} ${vehicle.make} ${vehicle.model}${vehicle.trim_package ? ' ' + vehicle.trim_package : ''}`.trim()
    : '…';

  return (
    <div className="ep-root">

      {/* ── Header ────────────────────────────────────────────────── */}
      <header className="ep-header">
        <div className="ep-header-brand">
          <span className="ep-header-gear">⚙</span>
          <span className="ep-header-name">AutoRestoration</span>
        </div>
        <div className="ep-header-user">
          <span className="ep-username">@{user?.username}</span>
          <button className="ep-signout-btn" onClick={handleSignOut}>Sign Out</button>
        </div>
      </header>

      <div className="ep-body">

        <div className="ep-page-header">
          <button className="ep-back-btn" onClick={() => navigate('/projects')}>
            ← My Projects
          </button>
          <div>
            <h1 className="ep-page-title">
              🔧 Edit Project
            </h1>
            <p className="ep-vehicle-subtitle">{vehicleLabel}</p>
          </div>
        </div>

        {loading ? (
          <p className="ep-loading">Loading…</p>
        ) : (
          <div className="ep-card">

            {/* Read-only identity block */}
            <div className="ep-identity">
              <div className="ep-identity-row">
                <span className="ep-identity-label">Year</span>
                <span className="ep-identity-value">{vehicle?.year}</span>
              </div>
              <div className="ep-identity-row">
                <span className="ep-identity-label">Make</span>
                <span className="ep-identity-value">{vehicle?.make}</span>
              </div>
              <div className="ep-identity-row">
                <span className="ep-identity-label">Model</span>
                <span className="ep-identity-value">{vehicle?.model}</span>
              </div>
              {vehicle?.trim_package && (
                <div className="ep-identity-row">
                  <span className="ep-identity-label">Package</span>
                  <span className="ep-identity-value">{vehicle.trim_package}</span>
                </div>
              )}
            </div>

            <p className="ep-identity-note">
              Year, make, model, and package are set from the reference catalog and cannot be edited here.
            </p>

            <hr className="ep-divider" />

            {/* Editable fields */}
            <div className="ep-fields">

              <div className="ep-field">
                <label className="ep-label">Project Nickname</label>
                <input
                  className="ep-input"
                  placeholder='e.g. "The Z28"'
                  value={form.nickname}
                  onChange={e => setForm(f => ({ ...f, nickname: e.target.value }))}
                />
              </div>

              <div className="ep-field">
                <label className="ep-label">Engine Description</label>
                <input
                  className="ep-input"
                  placeholder='e.g. "302 V8 DZ"'
                  value={form.engine_desc}
                  onChange={e => setForm(f => ({ ...f, engine_desc: e.target.value }))}
                />
              </div>

              <div className="ep-field">
                <label className="ep-label">Transmission</label>
                <input
                  className="ep-input"
                  placeholder='e.g. "Muncie 4-speed M22"'
                  value={form.transmission_desc}
                  onChange={e => setForm(f => ({ ...f, transmission_desc: e.target.value }))}
                />
              </div>

              <div className="ep-field">
                <label className="ep-label">VIN</label>
                <input
                  className="ep-input"
                  placeholder="Vehicle identification number"
                  value={form.vin}
                  onChange={e => setForm(f => ({ ...f, vin: e.target.value }))}
                />
              </div>

            </div>

            {error   && <p className="ep-error">{error}</p>}
            {success && <p className="ep-success">{success}</p>}

            <div className="ep-actions">
              <button className="ep-cancel-btn" onClick={() => navigate('/projects')}>
                Cancel
              </button>
              <button className="ep-save-btn" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : '💾 Save Changes'}
              </button>
            </div>

          </div>
        )}
      </div>
    </div>
  );
}
